import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Clock,
  Tag as TagIcon,
  Check,
  Copy,
  Trash2,
} from 'lucide-react';

import { docsService } from '../../services/docs/docsService';
import type {
  DocsProcedure,
  ProcedureRevision,
  DocsTag,
  ProcedureProcessLink,
} from '../../services/docs/types';
import { useAuth } from '../../hooks/useAuth';
import { ADMIN_CLEARANCE_LEVEL } from '../../services/types';

const PROCEDURE_TYPE_LABELS: Record<string, string> = {
  procedure: 'Procedure',
  engineering_document: 'Engineering Document',
  form: 'Form',
  policy: 'Policy',
  manual: 'Manual',
};

const DocsProcedurePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { clearanceLevel } = useAuth();
  const isAdmin =
    typeof clearanceLevel === 'number' &&
    clearanceLevel >= ADMIN_CLEARANCE_LEVEL;
  const [procedure, setProcedure] = useState<DocsProcedure | null>(null);
  const [revision, setRevision] = useState<ProcedureRevision | null>(null);
  const [tags, setTags] = useState<DocsTag[]>([]);
  const [linkedProcesses, setLinkedProcesses] = useState<
    ProcedureProcessLink[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;

    const fetchProcedure = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await docsService.getProcedure(slug);
        setProcedure(data.procedure);
        setRevision(data.revision);
        setTags(data.tags);
        setLinkedProcesses(data.linkedProcesses);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load procedure'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProcedure();
  }, [slug]);

  const handlePublish = async () => {
    if (!procedure) return;
    setPublishing(true);
    try {
      await docsService.approveProcedureRevision(procedure.id);
      const data = await docsService.getProcedure(procedure.slug);
      setProcedure(data.procedure);
      setRevision(data.revision);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!procedure) return;
    if (!window.confirm(`Delete "${procedure.title}"? This cannot be undone.`))
      return;
    setDeleting(true);
    try {
      await docsService.deleteProcedure(procedure.id);
      navigate('/docs');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
      setDeleting(false);
    }
  };

  const handleCopyId = () => {
    if (!procedure) return;
    navigator.clipboard.writeText(procedure.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !procedure) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error || 'Procedure not found'}</p>
          <button
            onClick={() => navigate('/docs')}
            className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
          >
            Back to Docs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {procedure.title}
            </h1>
            {procedure.summary && (
              <p className="mt-1 text-gray-600">{procedure.summary}</p>
            )}
            <button
              onClick={handleCopyId}
              className="flex items-center gap-1 mt-1 text-xs text-gray-400 hover:text-gray-600 font-mono"
              title="Copy document ID"
            >
              <Copy className="w-3 h-3" />
              {copied ? 'Copied!' : procedure.id}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && procedure.status !== 'published' && (
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center px-3 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-400"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {publishing ? 'Publishing...' : 'Publish'}
              </button>
            )}
            <button
              onClick={() =>
                navigate(`/docs/procedures/${procedure.slug}/edit`)
              }
              className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Edit className="w-4 h-4 mr-1.5" />
              Edit
            </button>
            {isAdmin && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center px-3 py-2 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </div>

        {/* Status and meta */}
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              procedure.status === 'published'
                ? 'bg-green-100 text-green-800'
                : procedure.status === 'draft'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {procedure.status.replace('_', ' ')}
          </span>
          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
            {PROCEDURE_TYPE_LABELS[procedure.type] || procedure.type}
          </span>
          {revision && (
            <span className="flex items-center">
              <Clock className="w-3.5 h-3.5 mr-1" />
              Rev {revision.revisionNumber || 'Draft'}
            </span>
          )}
          <span>
            Updated {new Date(procedure.updatedAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Content */}
      {revision && revision.contentPlainText && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 prose prose-sm max-w-none">
          <div className="whitespace-pre-wrap">{revision.contentPlainText}</div>
        </div>
      )}

      {!revision?.contentPlainText && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400 text-sm mb-6">
          No content yet. Click Edit to add content.
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <TagIcon className="w-4 h-4 text-gray-400" />
            {tags.map((tag) => (
              <span
                key={tag.id}
                className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Linked processes */}
      {linkedProcesses.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-2">
            Linked to processes:
          </h2>
          <div className="flex flex-wrap gap-2">
            {linkedProcesses.map((link) => (
              <button
                key={link.id}
                onClick={() => navigate(`/docs/processes/${link.processId}`)}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {link.linkLabel || link.processId}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocsProcedurePage;
